import { UPLOAD_API } from '../config/uploadApi';
import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef, type ReactNode } from 'react';
import axios, { type AxiosResponse } from 'axios';
import { isPersonalAdminRequest } from './personalAdminScope';
import { getLabelForUrl, shouldInterceptGhostChange } from './ghostChangeTrackerPolicy';

/* ─── Types ─── */
export interface PendingChange {
    id: string;
    timestamp: Date;
    label: string;
    method: string;
    url: string;
    /** The full axios request config needed to replay this request */
    requestConfig: any;
    /** Whether the user has checked this change for application */
    checked: boolean;
    /** Status after apply attempt */
    applyStatus?: 'pending' | 'applying' | 'applied' | 'error';
    errorMessage?: string;
}

interface GhostChangeTrackerContextType {
    /** Queued (deferred) changes */
    pending: PendingChange[];
    /** Toggle checked state */
    toggleCheck: (id: string) => void;
    /** Check/uncheck all */
    setAllChecked: (checked: boolean) => void;
    /** Apply all checked changes (execute real API calls) */
    applyChecked: () => Promise<void>;
    /** Discard all pending changes */
    discardAll: () => void;
    /** Whether interception is active */
    isActive: boolean;
    /** Whether we're currently applying */
    isApplying: boolean;
}

const GhostChangeTrackerContext = createContext<GhostChangeTrackerContextType | undefined>(undefined);

/* ─── Provider ─── */
export function GhostChangeTrackerProvider({ children, isAdmin, personalUserId }: { children: ReactNode; isAdmin: boolean; personalUserId?: string }) {
    const [pending, setPending] = useState<PendingChange[]>([]);
    const [isApplying, setIsApplying] = useState(false);
    const approvedUploads = useRef(new Set<string>());
    const deferred = useRef(new Map<string, { resolve: (response: any) => void; reject: (error: any) => void }>());
    const isAdminRef = useRef(isAdmin);
    const isApplyingRef = useRef(false);
    isAdminRef.current = isAdmin;

    const discardAll = useCallback(() => {
        for (const item of deferred.current.values()) item.reject(new axios.CanceledError('Personal change discarded'));
        deferred.current.clear(); setPending([]);
    }, []);
    useEffect(() => () => {
        for (const item of deferred.current.values()) item.reject(new axios.CanceledError('Personal Space closed'));
        deferred.current.clear();
    }, []);

    const toggleCheck = useCallback((id: string) => {
        setPending(prev => prev.map(p => p.id === id ? { ...p, checked: !p.checked } : p));
    }, []);

    const setAllChecked = useCallback((checked: boolean) => {
        setPending(prev => prev.map(p => ({ ...p, checked })));
    }, []);

    const applyChecked = useCallback(async () => {
        if (isApplyingRef.current) return;
        setIsApplying(true);
        isApplyingRef.current = true;

        const toApply = pending.filter(p => p.checked && p.applyStatus !== 'applied');

        for (const change of toApply) {
            setPending(prev => prev.map(p => p.id === change.id ? { ...p, applyStatus: 'applying' } : p));
            try {
                // Re-execute the original request (without the ghost adapter override)
                const config = { ...change.requestConfig };
                if (personalUserId) config.headers = { ...config.headers, 'x-personal-user': personalUserId, 'x-personal-confirmed': 'true' };
                delete config.adapter; // remove the fake adapter so the real one is used
                const response = await axios.request(config);
                if (personalUserId && /\/chunked-upload\/init$/.test(new URL(config.url, window.location.origin).pathname) && response.data?.uploadId) approvedUploads.current.add(response.data.uploadId);
                deferred.current.get(change.id)?.resolve(response);
                deferred.current.delete(change.id);
                setPending(prev => prev.map(p => p.id === change.id ? { ...p, applyStatus: 'applied' } : p));
            } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Failed';
                setPending(prev => prev.map(p => p.id === change.id ? { ...p, applyStatus: 'error', errorMessage: msg } : p));
            }
        }

        isApplyingRef.current = false;
        setIsApplying(false);
    }, [pending, personalUserId]);

    useLayoutEffect(() => {
        if (!isAdmin) return;

        let nextId = 0;

        // Request interceptor: intercept mutations, queue them, return fake success
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                const method = (config.method || '').toLowerCase();
                const url = config.url || '';

                if (personalUserId && isPersonalAdminRequest(url, import.meta.env.VITE_API_URL || 'http://localhost:3001', window.location.origin, UPLOAD_API)) config.headers.set('x-personal-user', personalUserId);
                const uploadPart = new URL(url, window.location.origin).pathname.match(/\/chunked-upload\/([^/]+)\/(chunk|complete)$/);
                if (personalUserId && uploadPart && approvedUploads.current.has(uploadPart[1]) && config.headers?.['x-personal-user'] === personalUserId) config.headers.set('x-personal-confirmed', 'true');
                const personalTarget = config.headers?.['x-personal-user'];
                if (personalTarget && !personalUserId) return config;
                if (personalUserId && personalTarget !== personalUserId) return config;
                const intercept = personalUserId ? ['post', 'put', 'patch', 'delete'].includes(method) : shouldInterceptGhostChange(method, url);

                // Don't intercept during apply phase, or non-mutations, or skip-listed URLs
                if ((personalUserId ? config.headers?.['x-personal-confirmed'] === 'true' : isApplyingRef.current) || !isAdminRef.current || !intercept) {
                    return config;
                }

                // Store the original request config
                const changeId = `gc-${++nextId}-${Date.now()}`;
                const upperMethod = method.toUpperCase();

                setPending(prev => [...prev, {
                    id: changeId,
                    timestamp: new Date(),
                    label: getLabelForUrl(upperMethod, url),
                    method: upperMethod,
                    url,
                    requestConfig: { ...config },
                    checked: true,
                    applyStatus: 'pending',
                }]);

                // Team mode preserves its optimistic response; personal mode waits for explicit approval.
                config.adapter = () => {
                    if (personalUserId) return new Promise((resolve, reject) => { deferred.current.set(changeId, { resolve, reject }); });
                    return Promise.resolve({
                        data: { ok: true, __ghostQueued: true },
                        status: 200,
                        statusText: 'OK',
                        headers: {},
                        config,
                    } as AxiosResponse);
                };

                return config;
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
        };
    }, [isAdmin, personalUserId]);

    return (
        <GhostChangeTrackerContext.Provider value={{
            pending,
            toggleCheck,
            setAllChecked,
            applyChecked,
            discardAll,
            isActive: isAdmin,
            isApplying,
        }}>
            {children}
        </GhostChangeTrackerContext.Provider>
    );
}

export const useGhostChangeTracker = () => {
    const context = useContext(GhostChangeTrackerContext);
    if (context === undefined) {
        throw new Error('useGhostChangeTracker must be used within a GhostChangeTrackerProvider');
    }
    return context;
};
